import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { AuthLoadingScreen } from "../../components";
import { RedirectingScreen } from "../../components/auth/AuthHandoffScreen";
import { useAuth } from "../../contexts/AuthContext";
import apiService from "../../shared/apiService";
import { peekPostLoginPath } from "../../shared/postLoginRedirect";

/**
 * Domínio que pode receber o token de sessão do usuário (SSO para outros produtos Virtus).
 * Exige o domínio exato ou um subdomínio real — `endsWith("virtusautomation.com")` sozinho
 * também aceitaria "evilvirtusautomation.com".
 */
const isTrustedSsoTarget = (url: URL): boolean =>
  url.hostname === "localhost" ||
  url.hostname === "virtusautomation.com" ||
  url.hostname.endsWith(".virtusautomation.com");

/**
 * SSO para o Virtus WhatsApp (CRM): o CRM não aceita o access_token do SMM, e sim um token
 * dedicado de curta duração (mesmo endpoint usado por `openVirtusCrmSSO`). Se não for possível
 * gerá-lo, o usuário segue para o login do CRM normalmente.
 */
const CrmSsoRedirect: React.FC<{ target: URL }> = ({ target }) => {
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiService.post<{ sso_token: string }>(
          "/api/auth/sso-token",
          {},
        );
        if (cancelled) return;
        window.location.href = `${target.origin}/sso?token=${encodeURIComponent(
          data.sso_token,
        )}&redirect=${encodeURIComponent("/dashboard")}`;
      } catch (e) {
        console.error("Falha ao gerar o SSO do CRM", e);
        if (!cancelled) window.location.href = target.toString();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [target]);

  return <RedirectingScreen />;
};

export interface PublicRouteProps {
  children: React.ReactNode;
}

export const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const { isAuthenticated, user, token, isLoading } = useAuth();
  const location = useLocation();

  const searchParams = new URLSearchParams(window.location.search);
  // Links de e-mail (redefinir senha, confirmar e-mail) abrem mesmo com sessão ativa
  const isEmailLinkPage =
    location.pathname === "/auth/reset-password" ||
    location.pathname === "/auth/confirm-email";

  // Nas telas de autenticação, o formulário cuida do seu próprio loading
  if (isLoading && !location.pathname.startsWith("/auth")) {
    return <AuthLoadingScreen message="Verificando autenticação..." />;
  }

  if (isEmailLinkPage) {
    return <>{children}</>;
  }

  if (isAuthenticated && user && token) {
    const redirectUri = searchParams.get("redirect_uri");
    if (redirectUri) {
      try {
        const url = new URL(redirectUri);

        // SSO: o produto de destino (ex.: Virtus Leadflow) pede a sessão explicitamente com
        // `sso=1`. O token só é entregue a domínios Virtus confiáveis; sem `sso=1` o
        // comportamento continua o mesmo de antes (redirecionamento simples).
        if (searchParams.get("sso") === "crm" && isTrustedSsoTarget(url)) {
          return <CrmSsoRedirect target={url} />;
        }

        if (searchParams.get("sso") === "1" && isTrustedSsoTarget(url)) {
          url.searchParams.set("sso_token", token);
          window.location.href = url.toString();
          return <RedirectingScreen />;
        }

        if (
          url.hostname.endsWith("virtusautomation.com") ||
          url.hostname === "localhost"
        ) {
          window.location.href = redirectUri;
          return <RedirectingScreen />;
        }
      } catch (e) {
        console.error("Invalid redirect_uri", e);
      }
    }
    // Veio do menu de aplicações sem sessão: continua para a aplicação escolhida.
    return <Navigate to={peekPostLoginPath() ?? "/"} replace />;
  }

  return <>{children}</>;
};
